/*
  ZEVQILO Atelier — marquee.js
  <zq-marquee>: an endless band. The first list is the real content; copies fill the width and are
  hidden from assistive technology and the keyboard (inert). Scrolling the page speeds the band up,
  and scrolling up turns it the other way. It pauses under the pointer, on keyboard focus and with the
  pause button, and stays still for visitors who prefer reduced motion.
*/
(() => {
  if (customElements.get('zq-marquee')) return;

  const reduceQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* Focus from the keyboard. A browser that does not know :focus-visible counts every focus. */
  const keyboardFocus = (el) => {
    try {
      return el.matches(':focus-visible');
    } catch (error) {
      return true;
    }
  };

  class ZqMarquee extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-marquee-track]');
      this.group = this.querySelector('[data-marquee-group]');
      this.toggle = this.querySelector('[data-marquee-toggle]');
      if (!this.track || !this.group || reduceQuery.matches) return;

      this.speed = Number(this.dataset.speed) || 64;
      this.rate = 1;
      this.direction = 1;
      this.velocity = 0;
      this.lastY = window.scrollY;
      this.holds = new Set();
      this.inView = true;

      this.tick = this.tick.bind(this);
      this.fill = this.fill.bind(this);
      this.onScroll = this.onScroll.bind(this);
      this.onToggle = this.onToggle.bind(this);
      this.onPointerEnter = (event) => event.pointerType === 'mouse' && this.setHold('pointer', true);
      this.onPointerLeave = () => this.setHold('pointer', false);
      /* Keyboard focus on a link holds the band still so the link does not move away. The pause button
         has its own say: focusing it (or clicking it) must not keep the band paused once it says play. */
      this.onFocusIn = (event) => this.setHold('focus', event.target !== this.toggle && keyboardFocus(event.target));
      this.onFocusOut = (event) => !this.contains(event.relatedTarget) && this.setHold('focus', false);

      this.addEventListener('pointerenter', this.onPointerEnter);
      this.addEventListener('pointerleave', this.onPointerLeave);
      this.addEventListener('focusin', this.onFocusIn);
      this.addEventListener('focusout', this.onFocusOut);
      this.toggle?.addEventListener('click', this.onToggle);
      if (this.hasAttribute('data-scroll-boost')) window.addEventListener('scroll', this.onScroll, { passive: true });

      this.resizeObserver = new ResizeObserver(() => {
        window.clearTimeout(this.resizeTimer);
        this.resizeTimer = window.setTimeout(this.fill, 120);
      });
      this.resizeObserver.observe(this);
      this.visibility = new IntersectionObserver(([entry]) => {
        this.inView = entry.isIntersecting;
        this.setHold('offscreen', !entry.isIntersecting);
      });
      this.visibility.observe(this);

      /* Wait for the web fonts so the measured width is the real one. */
      (document.fonts?.ready || Promise.resolve()).then(this.fill);
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      this.resizeObserver?.disconnect();
      this.visibility?.disconnect();
      window.cancelAnimationFrame(this.frame);
      window.clearTimeout(this.resizeTimer);
    }

    fill() {
      if (!this.isConnected) return;
      this.track.querySelectorAll('[data-marquee-clone]').forEach((clone) => clone.remove());
      const width = this.group.getBoundingClientRect().width;
      if (!width) return;
      const copies = Math.max(1, Math.ceil(this.clientWidth / width));
      for (let i = 0; i < copies; i += 1) {
        const clone = this.group.cloneNode(true);
        clone.removeAttribute('data-marquee-group');
        clone.setAttribute('data-marquee-clone', '');
        clone.setAttribute('aria-hidden', 'true');
        clone.inert = true;
        clone.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));
        this.track.append(clone);
      }
      this.style.setProperty('--marquee-distance', `${width.toFixed(1)}px`);
      this.style.setProperty('--marquee-duration', `${(width / this.speed).toFixed(2)}s`);
      this.setAttribute('data-ready', '');
      if (this.toggle) this.toggle.hidden = false;
      this.animation = this.track.getAnimations()[0] || null;
      this.apply();
    }

    setHold(reason, on) {
      if (on) {
        this.holds.add(reason);
      } else {
        this.holds.delete(reason);
      }
      this.apply();
    }

    apply() {
      if (!this.animation) return;
      if (this.holds.size) {
        this.animation.pause();
      } else if (this.animation.playState !== 'running') {
        try {
          this.animation.play();
        } catch (error) {
          /* Nothing to resume. */
        }
      }
    }

    onToggle() {
      const paused = this.toggle.getAttribute('aria-pressed') !== 'true';
      this.toggle.setAttribute('aria-pressed', String(paused));
      this.setHold('button', paused);
    }

    /* Scrolling adds speed; the band eases back to its own pace. */
    onScroll() {
      const y = window.scrollY;
      const delta = y - this.lastY;
      this.lastY = y;
      if (!this.inView || !this.animation) return;
      if (Math.abs(delta) > 1) this.direction = delta > 0 ? 1 : -1;
      this.velocity = Math.min(60, Math.max(this.velocity, Math.abs(delta)));
      if (!this.frame) this.frame = window.requestAnimationFrame(this.tick);
    }

    tick() {
      this.velocity *= 0.9;
      const target = this.direction * (1 + this.velocity / 7);
      this.rate += (target - this.rate) * 0.15;
      if (this.animation) {
        /* Running backwards, an endless animation would stop at its start: keep well clear of it. */
        const period = this.animation.effect?.getComputedTiming().duration;
        if (this.rate < 0 && period && this.animation.currentTime < period * 4) this.animation.currentTime += period * 1000;
        this.animation.updatePlaybackRate(Number(this.rate.toFixed(3)));
      }
      const settled = this.velocity < 0.05 && Math.abs(target - this.rate) < 0.01;
      this.frame = settled ? 0 : window.requestAnimationFrame(this.tick);
    }
  }

  customElements.define('zq-marquee', ZqMarquee);
})();
